import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { supabase } from "../config";

const TZ = {
  CST: { key: "CST", label: "CST", iana: "America/Chicago" },
  IST: { key: "IST", label: "IST", iana: "Asia/Kolkata" },
};

const SessionDetail = () => {
  const { sessionTitle } = useParams();
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [host, setHost] = useState(null);
  const [analyst, setAnalyst] = useState(null);
  const [storytellers, setStorytellers] = useState([]);
  const [allSessions, setAllSessions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [tzKey, setTzKey] = useState("CST");
  const activeTZ = TZ[tzKey];

  const timeFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat("en-US", {
        timeZone: activeTZ.iana,
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
    [activeTZ.iana]
  );

  const dateShortFormatter = useMemo(
    () =>
      new Intl.DateTimeFormat("en-US", {
        timeZone: activeTZ.iana,
        month: "short",
        day: "numeric",
      }),
    [activeTZ.iana]
  );

  useEffect(() => {
    fetchSessionData();
  }, [sessionTitle]);

  useEffect(() => {
    if (session) {
      setTzKey(session.day === 1 ? "CST" : "IST");
    }
  }, [session?.day]);

  const fetchSessionData = async () => {
    try {
      setIsLoading(true);

      // 1. Fetch session
      const { data: sessData, error: sessErr } = await supabase
        .from("sessions")
        .select("*")
        .eq("session_title", sessionTitle)
        .single();

      if (sessErr || !sessData) {
        setNotFound(true);
        return;
      }
      setSession(sessData);

      // Fetch all sessions for navigation
      const { data: allSess } = await supabase
        .from("sessions")
        .select("session_id, session_title, session_honor_title_telugu, session_honor_title_english, day")
        .order("session_id", { ascending: true });
      if (allSess) setAllSessions(allSess);

      // 2. Fetch host & analyst
      const personIds = [sessData.host_id, sessData.analyst_id].filter(Boolean);
      if (personIds.length > 0) {
        const { data: people } = await supabase
          .from("storytellers")
          .select("storyteller_id, storyteller_telugu, storyteller_english, photo_url, introduction")
          .in("storyteller_id", personIds);
        if (people) {
          people.forEach((p) => {
            if (p.storyteller_id === sessData.host_id) setHost(p);
            if (p.storyteller_id === sessData.analyst_id) setAnalyst(p);
          });
        }
      }

      // 3. Fetch slots in session time range, then their storytellers
      const { data: slots } = await supabase
        .from("slots")
        .select("slot_id, slot_from, slot_to, is_booked")
        .gte("slot_from", sessData.session_from)
        .lt("slot_from", sessData.session_to)
        .eq("is_booked", true)
        .order("slot_from", { ascending: true });

      if (slots && slots.length > 0) {
        const slotIds = slots.map((s) => s.slot_id);
        const { data: tellers } = await supabase
          .from("storytellers")
          .select("*")
          .in("slot_id", slotIds);

        if (tellers) {
          // Merge slot time info into storyteller records
          const slotMap = {};
          slots.forEach((s) => (slotMap[s.slot_id] = s));
          const merged = tellers.map((t) => ({
            ...t,
            slot: slotMap[t.slot_id],
          }));
          merged.sort(
            (a, b) => new Date(a.slot?.slot_from || 0) - new Date(b.slot?.slot_from || 0)
          );
          setStorytellers(merged);
        }
      }
    } catch (err) {
      console.error("Error fetching session:", err);
      setNotFound(true);
    } finally {
      setIsLoading(false);
    }
  };

  const formatTime = (utcStr) => timeFormatter.format(new Date(utcStr));
  const formatDate = (utcStr) => dateShortFormatter.format(new Date(utcStr));

  // Formatters for both timezones (always show both)
  const cstFmt = useMemo(
    () =>
      new Intl.DateTimeFormat("en-US", {
        timeZone: "America/Chicago",
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
    []
  );
  const istFmt = useMemo(
    () =>
      new Intl.DateTimeFormat("en-US", {
        timeZone: "Asia/Kolkata",
        weekday: "short",
        month: "short",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      }),
    []
  );

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-xl text-gray-600 animate-pulse">Loading session...</div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-gray-100 flex flex-col items-center justify-center">
        <h1 className="text-2xl font-bold text-gray-800 mb-4">Session Not Found</h1>
        <p className="text-gray-600 mb-6">
          No session found for "<span className="font-mono">{sessionTitle}</span>"
        </p>
        <button
          onClick={() => navigate("/sessions")}
          className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          View All Sessions
        </button>
      </div>
    );
  }

  const cstFrom = cstFmt.format(new Date(session.session_from));
  const cstTo = cstFmt.format(new Date(session.session_to));
  const istFrom = istFmt.format(new Date(session.session_from));
  const istTo = istFmt.format(new Date(session.session_to));

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Poster */}
      {session.poster_image_url ? (
        <img
          src={session.poster_image_url}
          alt={session.session_honor_title_english}
          className="w-full max-w-5xl mx-auto aspect-video object-cover"
        />
      ) : (
        <div className="w-full max-w-5xl mx-auto aspect-video bg-gradient-to-br from-gray-800 to-gray-950 flex flex-col items-center justify-center text-white p-8">
          <span className="text-2xl md:text-4xl font-bold mb-2">Session {session.session_id}</span>
          <span className="text-3xl md:text-5xl font-bold mb-2">
            {session.session_honor_title_telugu}
          </span>
          <span className="text-xl md:text-2xl font-medium opacity-80">
            {session.session_honor_title_english}
          </span>
          <div className="mt-4 text-base md:text-lg opacity-70 space-x-6">
            <span>🇺🇸 {cstFrom} – {cstTo} CST</span>
            <span>🇮🇳 {istFrom} – {istTo} IST</span>
          </div>
        </div>
      )}

      {/* Session Navigation — right below poster */}
      <div className="max-w-5xl mx-auto px-4 pt-4">
        {allSessions.length > 0 && (
          <SessionNav
            allSessions={allSessions}
            currentTitle={sessionTitle}
            navigate={navigate}
          />
        )}
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Navigation */}
        <div className="flex items-center justify-between mb-6">
          <button
            onClick={() => navigate("/sessions")}
            className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 text-sm font-medium"
          >
            ← All Sessions
          </button>
          <div className="flex rounded-md overflow-hidden border border-gray-300">
            {["CST", "IST"].map((tz) => (
              <button
                key={tz}
                onClick={() => setTzKey(tz)}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  tzKey === tz
                    ? "bg-blue-600 text-white"
                    : "bg-white text-gray-700 hover:bg-gray-100"
                }`}
              >
                {tz}
              </button>
            ))}
          </div>
        </div>

        {/* Session Info Card — compact */}
        <div className="bg-white rounded-xl shadow-sm px-5 py-3 mb-4">
          {/* Line 1: Telugu title + Host + Analyst + YouTube + Facebook */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
            <h1 className="text-xl md:text-2xl font-bold text-gray-800">
              {session.session_honor_title_telugu}
            </h1>
            {host && (
              <span className="flex items-center gap-1.5 text-sm">
                {host.photo_url && <img src={host.photo_url} alt="" className="w-6 h-6 rounded-full object-cover" />}
                <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full bg-green-100 text-green-800">🎤 {host.storyteller_english}</span>
              </span>
            )}
            {analyst && (
              <span className="flex items-center gap-1.5 text-sm">
                {analyst.photo_url && <img src={analyst.photo_url} alt="" className="w-6 h-6 rounded-full object-cover" />}
                <span className="text-xs font-semibold px-1.5 py-0.5 rounded-full bg-purple-100 text-purple-800">📝 {analyst.storyteller_english}</span>
              </span>
            )}
            {session.youtube_live_url && (
              <a href={session.youtube_live_url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-3 py-1 bg-red-600 text-white rounded-md hover:bg-red-700 font-medium text-xs transition-colors">
                ▶ YouTube
              </a>
            )}
            {session.facebook_live_url && (
              <a href={session.facebook_live_url} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1 px-3 py-1 bg-blue-700 text-white rounded-md hover:bg-blue-800 font-medium text-xs transition-colors">
                📘 Facebook
              </a>
            )}
          </div>
          {/* Line 2: English title + timestamps */}
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
            <h2 className="text-base text-gray-600">{session.session_honor_title_english}</h2>
            <span className="text-xs text-gray-500">🇺🇸 {cstFrom} – {cstTo} CST</span>
            <span className="text-xs text-gray-500">🇮🇳 {istFrom} – {istTo} IST</span>
          </div>
        </div>

        {/* Storytellers */}
        <div className="mb-6">
          <h3 className="text-lg font-bold text-gray-800 mb-4">
            Storytellers ({storytellers.length})
          </h3>
          {storytellers.length === 0 ? (
            <p className="text-gray-500 italic">No storytellers registered for this session yet.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {storytellers.map((teller) => (
                <div
                  key={teller.storyteller_id || teller.slot_id}
                  className="bg-white rounded-lg shadow-sm border border-gray-100 p-4 hover:shadow-md transition-shadow relative"
                >
                  <span className="absolute top-2 right-2 text-[10px] text-gray-300 font-mono">{teller.slot_id}</span>
                  <div className="flex items-start gap-3">
                    {teller.photo_url ? (
                      <img
                        src={teller.photo_url}
                        alt={teller.storyteller_english}
                        className="w-14 h-14 rounded-full object-cover shrink-0 border-2 border-gray-200"
                      />
                    ) : (
                      <div className="w-14 h-14 rounded-full bg-gray-200 flex items-center justify-center text-gray-500 text-xl shrink-0">
                        👤
                      </div>
                    )}
                    <div className="min-w-0">
                      <div className="font-bold text-gray-800 text-base">
                        {teller.storyteller_telugu}
                      </div>
                      <div className="text-sm text-gray-600">{teller.storyteller_english}</div>
                      {teller.writer_story_title && (
                        <div className="text-xs text-gray-500 mt-1 italic">
                          {teller.writer_story_title}
                        </div>
                      )}
                    </div>
                  </div>
                  {teller.slot && (
                    <div className="mt-2 text-xs text-gray-500 border-t border-gray-100 pt-2">
                      ⏰ {formatDate(teller.slot.slot_from)} {formatTime(teller.slot.slot_from)} – {formatTime(teller.slot.slot_to)} {activeTZ.label}
                    </div>
                  )}
                  {/* Story Title & Writer */}
                  <div className="mt-2 space-y-1 border-t border-gray-100 pt-2">
                    <EditableField teller={teller} field="story_title" label="కథ పేరు:" placeholder="కథ పేరు" requireTelugu />
                    <EditableField teller={teller} field="story_writer" label="రచయిత:" placeholder="రచయిత పేరు" requireTelugu />
                  </div>
                  {/* Story Start Time */}
                  <StoryStartTime teller={teller} youtubeUrl={session.youtube_live_url} />
                  {teller.introduction && (
                    <div className="mt-2 text-xs text-gray-600 line-clamp-3">
                      {teller.introduction}
                    </div>
                  )}
                  {/* Story Analysis Section */}
                  {(teller.analysis_text || teller.analysis_image1_url || teller.analysis_image2_url) && (
                    <StoryAnalysis teller={teller} />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

      </div>
    </div>
  );
};

/* ─── Story Analysis (expandable per storyteller) ─── */
const StoryAnalysis = ({ teller }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="mt-3 border-t border-gray-100 pt-2">
      <button
        onClick={() => setExpanded((prev) => !prev)}
        className="flex items-center gap-1.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 transition-colors"
      >
        <span className={`transform transition-transform ${expanded ? "rotate-90" : ""}`}>▶</span>
        కథా విశ్లేషణ / Story Analysis
      </button>

      {expanded && (
        <div className="mt-2 space-y-2 animate-in fade-in">
          {/* Images: side by side if two, full width if one */}
          {(teller.analysis_image1_url || teller.analysis_image2_url) && (
            <div className={`grid gap-2 ${teller.analysis_image1_url && teller.analysis_image2_url ? 'grid-cols-2' : 'grid-cols-1'}`}>
              {teller.analysis_image1_url && (
                <a href={teller.analysis_image1_url} target="_blank" rel="noopener noreferrer">
                  <img
                    src={teller.analysis_image1_url}
                    alt="Visual 1"
                    className="w-full rounded-md border border-gray-200 hover:shadow-md transition-shadow cursor-zoom-in"
                  />
                </a>
              )}
              {teller.analysis_image2_url && (
                <a href={teller.analysis_image2_url} target="_blank" rel="noopener noreferrer">
                  <img
                    src={teller.analysis_image2_url}
                    alt="Visual 2"
                    className="w-full rounded-md border border-gray-200 hover:shadow-md transition-shadow cursor-zoom-in"
                  />
                </a>
              )}
            </div>
          )}

          {/* Scrollable analysis text */}
          {teller.analysis_text && (
            <div className="max-h-40 overflow-y-auto bg-gray-50 rounded-md p-3 text-xs text-gray-700 leading-relaxed whitespace-pre-wrap border border-gray-200">
              {teller.analysis_text}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ─── Inline Editable Field ─── */
function isTeluguScript(text) {
  // Allow Telugu characters (U+0C00–U+0C7F), spaces, punctuation, and digits
  const stripped = text.replace(/[\s\d.,!?;:'"()\-–—]/g, "");
  if (!stripped) return false;
  return /^[\u0C00-\u0C7F]+$/.test(stripped);
}

const EditableField = ({ teller, field, label, placeholder, requireTelugu }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(teller[field] || "");
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    if (requireTelugu && value.trim() && !isTeluguScript(value.trim())) {
      alert("దయచేసి తెలుగులో నమోదు చేయండి / Please enter in Telugu script.");
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from("storytellers")
      .update({ [field]: value.trim() || null })
      .eq("slot_id", teller.slot_id);
    setSaving(false);
    if (error) {
      alert("Failed to save: " + error.message);
    } else {
      teller[field] = value.trim() || null;
      setEditing(false);
    }
  };

  const handleCancel = () => {
    setValue(teller[field] || "");
    setEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSave();
    if (e.key === "Escape") handleCancel();
  };

  return (
    <div className="flex items-center gap-1.5 text-xs text-gray-500">
      <span>{label}</span>
      {editing ? (
        <>
          <input
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={placeholder}
            className="flex-1 min-w-0 px-1.5 py-0.5 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-blue-400"
            autoFocus
          />
          <button onClick={handleSave} disabled={saving}
            className="text-green-600 hover:text-green-800 text-sm" title="Save">✓</button>
          <button onClick={handleCancel}
            className="text-red-500 hover:text-red-700 text-sm" title="Cancel">✕</button>
        </>
      ) : (
        <>
          <span className="font-semibold text-gray-700">{teller[field] || "—"}</span>
          <button onClick={() => setEditing(true)}
            className="text-gray-400 hover:text-gray-600 transition-colors" title={`Edit ${placeholder}`}>
            ✏️
          </button>
        </>
      )}
    </div>
  );
};

/* ─── Story Start Time (editable HH:MM:SS) + Direct Story Link ─── */
function hhmmssToSeconds(ts) {
  const parts = (ts || "00:00:00").split(":").map(Number);
  return (parts[0] || 0) * 3600 + (parts[1] || 0) * 60 + (parts[2] || 0);
}

function buildYouTubeTimestampUrl(baseUrl, ts) {
  if (!baseUrl) return null;
  const secs = hhmmssToSeconds(ts);
  // Strip any existing ?t= or &t= param
  const clean = baseUrl.replace(/[?&]t=\d+s?/, "");
  const sep = clean.includes("?") ? "&" : "?";
  return `${clean}${sep}t=${secs}s`;
}

const StoryStartTime = ({ teller, youtubeUrl }) => {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(teller.story_start_time || "00:00:00");
  const [saving, setSaving] = useState(false);
  const currentTime = teller.story_start_time || "00:00:00";
  const storyLink = buildYouTubeTimestampUrl(youtubeUrl, currentTime);

  const handleSave = async () => {
    // Validate HH:MM:SS format
    if (!/^\d{2}:\d{2}:\d{2}$/.test(value)) {
      alert("Please enter time in HH:MM:SS format");
      return;
    }
    setSaving(true);
    const { error } = await supabase
      .from("storytellers")
      .update({ story_start_time: value })
      .eq("slot_id", teller.slot_id);
    setSaving(false);
    if (error) {
      alert("Failed to save: " + error.message);
    } else {
      teller.story_start_time = value;
      setEditing(false);
    }
  };

  const handleCancel = () => {
    setValue(teller.story_start_time || "00:00:00");
    setEditing(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSave();
    if (e.key === "Escape") handleCancel();
  };

  return (
    <div className="mt-2 space-y-1">
      <div className="flex items-center gap-1.5 text-xs text-gray-500">
        <span>🎬 Story starts at</span>
        {editing ? (
          <>
            <input
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="HH:MM:SS"
              maxLength={8}
              className="w-20 px-1.5 py-0.5 border border-gray-300 rounded text-xs font-mono text-center focus:outline-none focus:ring-1 focus:ring-blue-400"
              autoFocus
            />
            <button onClick={handleSave} disabled={saving}
              className="text-green-600 hover:text-green-800 text-sm" title="Save">✓</button>
            <button onClick={handleCancel}
              className="text-red-500 hover:text-red-700 text-sm" title="Cancel">✕</button>
          </>
        ) : (
          <>
            <span className="font-mono font-semibold text-gray-700">{currentTime}</span>
            <button onClick={() => setEditing(true)}
              className="text-gray-400 hover:text-gray-600 transition-colors" title="Edit start time">
              ✏️
            </button>
          </>
        )}
      </div>
      {storyLink && (
        <a href={storyLink} target="_blank" rel="noopener noreferrer"
          className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-600 text-white rounded text-[11px] font-semibold hover:bg-red-700 transition-colors"
          title={storyLink}>
          <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.546 12 3.546 12 3.546s-7.505 0-9.377.504A3.016 3.016 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.504 9.376.504 9.376.504s7.505 0 9.377-.504a3.016 3.016 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
          Direct Story Link
        </a>
      )}
    </div>
  );
};

/* ─── Session Navigation (CD-style controls) ─── */
const SessionNav = ({ allSessions, currentTitle, navigate }) => {
  const currentIdx = allSessions.findIndex((s) => s.session_title === currentTitle);
  if (currentIdx === -1) return null;

  const total = allSessions.length;
  const prevSession = currentIdx > 0 ? allSessions[currentIdx - 1] : allSessions[total - 1];
  const nextSession = currentIdx < total - 1 ? allSessions[currentIdx + 1] : allSessions[0];
  const firstSession = allSessions[0];
  const lastSession = allSessions[total - 1];

  const goTo = (s) => navigate(`/sessions/${s.session_title}`);

  const NavBtn = ({ onClick, icon, label, sublabel, disabled, className = "" }) => (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all
        ${disabled
          ? "opacity-30 cursor-not-allowed"
          : "hover:bg-gray-100 active:scale-95"
        } ${className}`}
    >
      <span className="text-lg">{icon}</span>
      <span className="hidden sm:flex flex-col items-start leading-tight">
        <span className="text-gray-700">{label}</span>
        {sublabel && <span className="text-xs text-gray-400 truncate max-w-[120px]">{sublabel}</span>}
      </span>
    </button>
  );

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-3 mb-6">
      {/* Progress indicator */}
      <div className="flex items-center justify-center gap-1.5 mb-3">
        {allSessions.map((s, i) => (
          <button
            key={s.session_id}
            onClick={() => goTo(s)}
            title={`Session ${s.session_id}: ${s.session_honor_title_english || s.session_title}`}
            className={`h-2 rounded-full transition-all ${
              i === currentIdx
                ? "w-8 bg-blue-600"
                : "w-2 bg-gray-300 hover:bg-gray-400"
            }`}
          />
        ))}
      </div>

      {/* Controls */}
      <div className="flex items-center justify-between">
        <NavBtn
          onClick={() => goTo(firstSession)}
          icon="⏮"
          label="First"
          sublabel={firstSession.session_honor_title_english}
          disabled={currentIdx === 0}
        />
        <NavBtn
          onClick={() => goTo(prevSession)}
          icon="⏪"
          label="Previous"
          sublabel={prevSession.session_honor_title_english}
        />

        <div className="flex flex-col items-center px-3">
          <span className="text-xs text-gray-400 font-medium">
            Day {allSessions[currentIdx]?.day || ""} — SESSION
          </span>
          <span className="text-xl font-bold text-gray-800">
            {currentIdx + 1} <span className="text-sm text-gray-400 font-normal">/ {total}</span>
          </span>
        </div>

        <NavBtn
          onClick={() => goTo(nextSession)}
          icon="⏩"
          label="Next"
          sublabel={nextSession.session_honor_title_english}
        />
        <NavBtn
          onClick={() => goTo(lastSession)}
          icon="⏭"
          label="Last"
          sublabel={lastSession.session_honor_title_english}
          disabled={currentIdx === total - 1}
        />
      </div>
    </div>
  );
};

/* ─── Person Card (Host / Analyst) ─── */
const PersonCard = ({ person, role, roleColor, roleIcon }) => {
  const colorMap = {
    green: {
      bg: "bg-green-50",
      border: "border-green-200",
      badge: "bg-green-100 text-green-800",
    },
    purple: {
      bg: "bg-purple-50",
      border: "border-purple-200",
      badge: "bg-purple-100 text-purple-800",
    },
  };
  const colors = colorMap[roleColor] || colorMap.green;

  return (
    <div className={`${colors.bg} ${colors.border} border rounded-xl p-4 flex items-center gap-4`}>
      {person.photo_url ? (
        <img
          src={person.photo_url}
          alt={person.storyteller_english}
          className="w-16 h-16 rounded-full object-cover border-2 border-white shadow-sm"
        />
      ) : (
        <div className="w-16 h-16 rounded-full bg-white flex items-center justify-center text-2xl shadow-sm">
          {roleIcon}
        </div>
      )}
      <div>
        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${colors.badge}`}>
          {roleIcon} {role}
        </span>
        <div className="font-bold text-gray-800 text-lg mt-1">{person.storyteller_telugu}</div>
        <div className="text-sm text-gray-600">{person.storyteller_english}</div>
      </div>
    </div>
  );
};

export default SessionDetail;
